import * as React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { Skeleton } from '@/components/atoms/skeleton';
import { Card } from '@/components/atoms/card';
import { DataPagination } from '@/components/molecules/data-pagination';
import { useIsDesktop } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';

export interface ColumnDef<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (item: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
}

interface ResponsiveDataListProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  renderCard: (item: T, index: number) => React.ReactNode;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  // Row click
  onRowClick?: (item: T) => void;
  // Pagination
  page?: number;
  pageSize?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  // Card grid classes
  cardGridClassName?: string;
  // Skeleton counts
  skeletonCount?: number;
  renderCardSkeleton?: () => React.ReactNode;
  // Key extractor
  keyExtractor: (item: T, index: number) => string;
}

export function ResponsiveDataList<T>({
  data,
  columns,
  renderCard,
  isLoading = false,
  emptyState,
  onRowClick,
  page = 1,
  pageSize = 10,
  totalPages = 1,
  totalItems,
  onPageChange,
  onPageSizeChange,
  cardGridClassName = 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
  skeletonCount = 6,
  renderCardSkeleton,
  keyExtractor,
}: ResponsiveDataListProps<T>) {
  const isDesktop = useIsDesktop();
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn) return data;

    const column = columns.find((c) => c.id === sortColumn);
    if (!column?.accessorKey) return data;

    return [...data].sort((a, b) => {
      const aVal = a[column.accessorKey as keyof T];
      const bVal = b[column.accessorKey as keyof T];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortColumn, sortDirection, columns]);


  // Render loading skeleton
  if (isLoading) {
    if (isDesktop) {
      return (
        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead key={column.id} className={column.className}>
                      {column.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      );
    }

    // Mobile skeleton
    return (
      <div className={cardGridClassName}>
        {[...Array(skeletonCount)].map((_, i) =>
          renderCardSkeleton ? (
            <React.Fragment key={i}>{renderCardSkeleton()}</React.Fragment>
          ) : (
            <Card key={i} className="p-4">
              <div className="space-y-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            </Card>
          )
        )}
      </div>
    );
  }

  // Render empty state
  if (!data || data.length === 0) {
    return emptyState || null;
  }

  // Desktop: Table view
  if (isDesktop) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className={cn(
                      column.sortable && 'cursor-pointer select-none hover:bg-muted',
                      column.className
                    )}
                    onClick={column.sortable ? () => handleSort(column.id) : undefined}
                  >
                    <div className="flex items-center gap-2">
                      {column.header}
                      {column.sortable && sortColumn === column.id && (
                        sortDirection === 'asc' ? (
                          <ChevronUp className="size-4" />
                        ) : (
                          <ChevronDown className="size-4" />
                        )
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((item, index) => (
                <TableRow
                  key={keyExtractor(item, index)}
                  className={cn(onRowClick && 'cursor-pointer')}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} className={column.className}>
                      {column.cell
                        ? column.cell(item)
                        : column.accessorKey
                        ? String(item[column.accessorKey] ?? '')
                        : ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <DataPagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          variant="desktop"
        />
      </div>
    );
  }

  // Mobile: Card view
  return (
    <div className="space-y-4">
      <div className={cardGridClassName}>
        {data.map((item, index) => (
          <React.Fragment key={keyExtractor(item, index)}>
            {renderCard(item, index)}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile pagination */}
      <DataPagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        variant="mobile"
        className="pt-4"
      />
    </div>
  );
}
